import {createFeatureSelector, createSelector} from '@ngrx/store';
import * as fromProposal from '../reducers/proposal.reducer';
import {OfferStatus} from '../../../enum/offer-status';
import {selectUser, selectUserId} from '../../user/selectors/user.selectors';
import {selectUserPermissions} from '../../auth/selectors/auth.selectors';
import {Permissions} from '../../../enum/permissions';
import {OfferType} from '../../../enum/offer-type';

export const selectProposalFeature = createFeatureSelector<fromProposal.ProposalState>(
  fromProposal.proposalFeatureKey
);

export const selectProposals = createSelector(
  selectProposalFeature,
  state => state.proposalTiles
);

export const selectError = createSelector(
  selectProposalFeature,
  state => state.error
);

export const selectLoading = createSelector(
  selectProposalFeature,
  state => state.loading
);

export const selectAllTilesLoaded = createSelector(
  selectProposalFeature,
  state => state.allTilesLoaded
);

export const selectLoadingTiles = createSelector(
  selectProposalFeature,
  state => state.loadingTiles
);

export const selectProposalSelected = createSelector(
  selectProposalFeature,
  state => state.proposalSelected,
);

export const selectProposalSelectedId = createSelector(
  selectProposalFeature,
  state => state.selectedId,
);
export const selectCurrentFilter = createSelector(
  selectProposalFeature,
  state => state.activeFilters,
);
export const selectFilterToDisplay = createSelector(
  selectUserPermissions,
  selectUser,
  (permissions, userState) => permissions.includes(Permissions.offer_create) ? userState.lookingForLOBs : userState.offeringLOBs
);
export const selectDetailPageProposalTile = createSelector(
  selectProposalFeature,
  state => state.detailPageProposalTile,
);

export const   selectDetailPageProposalLoading
= createSelector(
  selectProposalFeature,
  state => state.loading
);

export const selectProposalTransmission = createSelector(
  selectProposalFeature,
  state => state.proposalTransmission,
);

export const selectDetailPageProposal = createSelector(
  selectProposalFeature,
  state => state?.detailPageProposal,
);

export const selectDetailPageProposalId = createSelector(
  selectDetailPageProposal,
  (proposal) => proposal?.proposalInformation?.id
);

export const selectDetailPageOffers = createSelector(
  selectProposalFeature,
  state => state.detailPageOffers?.filter(offer => ![OfferStatus.NOLONGERACTIVE.toString()].includes(offer.status)),
);

export const selectDetailPageOfferSelected = createSelector(
  selectProposalFeature,
  state => state.detailPageOfferSelected,
);

export const selectPreviewOffers = createSelector(
  selectProposalFeature,
  state => state.proposalSelectedOffers
);
export const selectPreviewUsersAllowedToView = createSelector(
  selectProposalFeature,
  state => state.proposalSelectedUsersAllowedToView
);
export const selectDetailPageCreateOfferHasLeading = createSelector(
  selectDetailPageProposal,
  (proposal) => {
    return proposal?.proposalInformation.hasLeading;
  }
);
export const selectDetailPageCreateOfferNeedsFollowing = createSelector(
  selectDetailPageProposal,
  (proposal) => {
    return proposal?.proposalInformation.followingInsurerNeeded;
  }
);
export const selectDetailPageHasRejected = createSelector(
  selectDetailPageProposal,
  selectDetailPageOffers,
  selectUserId,

  (proposal, offers, userId) => {
    return offers?.some(offer => offer.status === OfferStatus.REJECTION && offer.creator.id === userId) ?? false;
  }
);
export const selectRejectionId = createSelector(
  selectProposalSelected,
  selectPreviewOffers,
  selectUserId,

  (proposal, offers, userId) => {
    return offers?.filter(offer => offer.offerType === OfferType.REJECTION && offer.creator.id === userId).sort((a, b) => b.creationDate.getTime() - a.creationDate.getTime())[0]?.offerId;
  }
);
export const selectPreviewShowRetractRejection = createSelector(
  selectProposalSelected,
  selectPreviewOffers,
  selectUserId,

  (proposal, offers, userId) => {
    return offers.some(offer => offer.status === OfferStatus.REJECTION && offer.creator.id === userId);
  }
);
export const selectPreviewShowRejection = createSelector(
  selectProposalSelected,
  selectPreviewOffers,
  selectUserId,

  (proposal, offers, userId) => {
    return !offers.some(offer => (offer.status === OfferStatus.OPEN || offer.status === OfferStatus.ACCEPTED) && offer.creator.id === userId);
  }
);

export const selectProposalNumber = createSelector(
    selectProposalFeature,
    state => state.proposalNumber
);

export const selectRejectionLoading = createSelector(
  selectProposalFeature,
  state => state.rejectionLoading
);

export const selectRetractRejectionLoading = createSelector(
  selectProposalFeature,
  state => state.retractRejectionLoading
);

export const selectProposalPreview = createSelector(
  selectProposalFeature,
  (state) => state?.proposalPreview
);

export const selectProposalTilePreview = createSelector(
  selectProposalFeature,
  (state) => state.proposalTilePreview
);

export const selectMaxScrolledPreview = createSelector(
  selectProposalFeature,
  (state) => state.maxScrolled
);

export const selectDirtyFormPreview = createSelector(
  selectProposalFeature,
  (state) => state.isFormDirty
);

export const selectPrivateNotes = createSelector(
  selectProposalFeature,
  state => state?.privateNotes
);

